<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="modelValue"
        class="fixed inset-0 z-40 flex items-center justify-center"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="title ? modalTitleId : undefined"
      >
        <!-- Dimmed background -->
        <div
          class="absolute inset-0 bg-grey-1/75"
          aria-hidden="true"
          @click="$emit('update:modelValue', false)"
        ></div>
        <!-- Modal content -->
        <div
          ref="modalRef"
          :class="[
            'relative bg-white dark:bg-dark-surface lg:rounded-lg shadow-xl lg:max-w-3xl lg:mx-4 flex flex-col lg:h-auto lg:max-h-[90vh] w-full h-full',
          ]"
          tabindex="-1"
          @keydown.esc="$emit('update:modelValue', false)"
        >
          <!-- Title -->
          <div class="px-4 lg:px-8 py-8 flex shrink-0">
            <h3 v-if="title" :id="modalTitleId" class="text-2xl font-normal">{{ title }}</h3>
            <!-- Close button -->
            <AppButton
              v-if="!hideCloseIcon"
              theme="inherit"
              class="ml-auto"
              aria-label="Close modal"
              @click="$emit('update:modelValue', false)"
            >
              <mdicon name="close" size="23" />
            </AppButton>
          </div>
          <div
            v-if="$slots.body"
            class="px-4 lg:px-8 pt-0 pb-8 overflow-y-auto grow"
          >
            <slot name="body"></slot>
          </div>
          <!-- Footer -->
          <div
            class="px-4 lg:px-8 py-8 rounded-b-lg flex justify-end space-x-3 shrink-0 border-t border-grey-8 dark:border-dark-border"
          >
            <slot name="footer"></slot>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue'

const modalTitleId = ref(
  `modal-title-${Math.random().toString(36).substr(2, 9)}`
)

const modalRef = ref<HTMLElement | null>(null)

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    title?: string
    hideCloseIcon?: boolean
  }>(),
  {
    title: '',
    hideCloseIcon: false,
  }
)

defineEmits<{
  'update:modelValue': [value: boolean]
}>()

// Move focus into the dialog whenever it opens, including when it is
// mounted already open
watch(
  () => props.modelValue,
  async (newValue) => {
    if (newValue) {
      await nextTick()
      modalRef.value?.focus()
    }
  },
  { immediate: true }
)
</script>
